import { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { adminApi, type RefundRequestItem, type AuditLogEntry, type AuditLogPage, type MaintenanceState, type PcAdminItem, type PcAdminListResponse, type TotpEnrollData, type PendingAction, type PendingActionsList } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronRight,
  Search,
  ArrowLeft,
  Plus,
  Minus,
  ReceiptText,
  Shield,
  UserPlus,
  ShieldOff,
  KeyRound,
  ShieldCheck,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Hash,
  CreditCard,
  Package,
  Loader2,
  Copy,
  AlertTriangle,
  Mail,
  Crown,
  User as UserIcon,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AdminUser {
  id: number;
  steam_id: string;
  steam_name: string;
  steam_avatar: string | null;
  // null когда pc недоступен (fail-soft); UI рендерит «—»
  balance: number | null;
  // UUID payments-core wallet'а (для 4-eyes wallet_adjust action payload)
  wallet_id: string | null;
  email: string | null;
  phone: string | null;
  is_admin: boolean;
  created_at: string | null;
  last_activity: string | null;
}

interface AdminTx {
  id: number;
  type: string;
  amount: number;
  status: string;
  payment_method: string | null;
  payment_id: string | null;
  item_name: string | null;
  created_at: string | null;
}

interface UserDetail extends AdminUser {
  transactions: AdminTx[];
}

// Расширенная детализация одной транзакции (для drill-down в админке).
// Маппится 1:1 с pc /internal/v1/transactions/{tx_id} + monolith /api/v1/admin/transactions/{id}.
interface TxStateTransition {
  from_status: string | null;
  to_status: string;
  reason: string | null;
  actor: string | null;
  occurred_at: string;
}

interface TxPurchaseEmbed {
  purchase_id: string;
  product_id: string;
  status: string;
  xpanda_deal_id: string | null;
  xpanda_custom_id: string | null;
  trade_offer_id: string | null;
  delivered_at: string | null;
}

interface TxDetail {
  transaction_id: string;
  wallet_id: string;
  type: string;
  amount: number;          // RUB (мы конвертируем в backend из minor)
  amount_minor: number;
  currency: string;
  status: string;
  payment_method: string | null;
  payment_provider: string | null;
  payment_provider_id: string | null;
  created_at: string;
  completed_at: string | null;
  state_history: TxStateTransition[];
  purchase: TxPurchaseEmbed | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const REFUND_STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает',
  approved: 'Одобрен',
  rejected: 'Отклонён',
};

const REFUND_STATUS_COLOR: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const TX_STATUS_COLOR: Record<string, string> = {
  completed: 'text-green-400',
  paid: 'text-blue-400',
  pending: 'text-yellow-400',
  failed: 'text-red-400',
  cancelled: 'text-red-400',
  refunded: 'text-purple-400',
};

const TX_TYPE_LABEL: Record<string, string> = {
  deposit: 'Пополнение',
  purchase: 'Покупка',
  purchase_b2c: 'Покупка',
  sale: 'Продажа',
  refund: 'Возврат',
  reversal: 'Reversal',
  gift: 'Подарок',
};

const TX_STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает оплаты',
  paid: 'Оплачена',
  reserved: 'Резерв баланса',
  completed: 'Завершена',
  failed: 'Ошибка',
  cancelled: 'Отменена',
  refunded: 'Возвращена',
  released: 'Возврат резерва',
};

const PURCHASE_STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает',
  paid: 'Оплачено',
  reserved: 'Зарезервировано',
  deal_created: 'Сделка создана',
  trade_offered: 'Предложение выслано',
  delivered: 'Доставлено',
  failed: 'Ошибка',
  released: 'Возврат',
};

function fmtDateTime(s: string | null | undefined) {
  if (!s) return '—';
  return new Date(s).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const METHOD_LABEL: Record<string, string> = {
  invizo: 'Карта/СБП',
  balance: 'Баланс',
};

function fmtDate(s: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ── Refunds tab ───────────────────────────────────────────────────────────────

function RefundsTab() {
  const [requests, setRequests] = useState<RefundRequestItem[]>([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RefundRequestItem | null>(null);
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listRefundRequests(filterStatus || undefined);
      setRequests(data);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus]);

  // 4-eyes flow: refund-approve/reject теперь СОЗДАЁТ pending action.
  // Второй admin должен апрувить через таб «Approvals». TOTP-код вводится
  // в момент создания запроса (через TotpPromptDialog ниже).
  const [pendingKind, setPendingKind] = useState<'approve' | 'reject' | null>(null);

  const submitAction = async (totpCode: string) => {
    if (!selected || !pendingKind) return;
    setActionLoading(true);
    try {
      const actionType = pendingKind === 'approve' ? 'refund_approve' : 'refund_reject';
      await adminApi.createAction(
        actionType,
        { refund_id: selected.id, admin_note: note || null },
        totpCode,
      );
      toast.success(
        pendingKind === 'approve'
          ? 'Запрос на одобрение создан — нужен второй админ'
          : 'Запрос на отклонение создан — нужен второй админ',
      );
      setSelected(null);
      setNote('');
      setPendingKind(null);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = () => {
    if (!selected) return;
    setPendingKind('approve');
  };

  const handleReject = () => {
    if (!selected) return;
    setPendingKind('reject');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 flex-wrap">
          {['', 'pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                filterStatus === s
                  ? 'bg-[#ff6b00] border-[#ff6b00] text-white'
                  : 'border-[#30363d] text-[#8b949e] hover:border-[#ff6b00] hover:text-white'
              }`}
            >
              {s === '' ? 'Все' : REFUND_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <Button variant="ghost" className="text-[#8b949e]" onClick={load}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ff6b00]" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-[#8b949e]">Заявок нет</div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div
              key={r.id}
              className="bg-[#161b22] border border-[#30363d] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer hover:border-[#ff6b00]/40 transition-colors"
              onClick={() => { setSelected(r); setNote(''); }}
            >
              <div className="flex items-center gap-3 min-w-[140px]">
                <span className="text-[#8b949e] text-sm">#{r.id}</span>
                <Badge className={`text-xs border ${REFUND_STATUS_COLOR[r.status]}`}>
                  {REFUND_STATUS_LABEL[r.status]}
                </Badge>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{r.user?.steam_name || '—'}</p>
                <p className="text-[#8b949e] text-sm truncate">{r.transaction?.item_name || '—'}</p>
                {r.user_reason && (
                  <p className="text-[#8b949e] text-xs mt-0.5 truncate italic">«{r.user_reason}»</p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-[#ff6b00] font-semibold">{r.transaction?.amount} ₽</p>
                <p className="text-[#8b949e] text-xs">{METHOD_LABEL[r.transaction?.payment_method || ''] || r.transaction?.payment_method}</p>
                <p className="text-[#6e7681] text-xs">{fmtDate(r.created_at)}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#8b949e] shrink-0 hidden sm:block" />
            </div>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              Заявка #{selected?.id}
              {selected && (
                <Badge className={`text-xs border ${REFUND_STATUS_COLOR[selected.status]}`}>
                  {REFUND_STATUS_LABEL[selected.status]}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2">
              <div className="bg-[#0d1117] rounded-lg p-3 space-y-1">
                <p className="text-[#8b949e] text-xs uppercase tracking-wide">Пользователь</p>
                <p className="text-white font-medium">{selected.user?.steam_name || '—'}</p>
                {selected.user?.email && <p className="text-[#8b949e] text-sm">{selected.user.email}</p>}
              </div>
              <div className="bg-[#0d1117] rounded-lg p-3 space-y-1">
                <p className="text-[#8b949e] text-xs uppercase tracking-wide">Транзакция #{selected.transaction?.id}</p>
                <p className="text-white font-medium">{selected.transaction?.item_name || '—'}</p>
                <div className="flex gap-4 text-sm flex-wrap">
                  <span className="text-[#ff6b00] font-semibold">{selected.transaction?.amount} ₽</span>
                  <span className="text-[#8b949e]">{METHOD_LABEL[selected.transaction?.payment_method || ''] || selected.transaction?.payment_method}</span>
                  <span className="text-[#8b949e]">transID: {selected.transaction?.payment_id || '—'}</span>
                </div>
              </div>
              {selected.user_reason && (
                <div className="bg-[#0d1117] rounded-lg p-3">
                  <p className="text-[#8b949e] text-xs uppercase tracking-wide mb-1">Причина</p>
                  <p className="text-white text-sm italic">«{selected.user_reason}»</p>
                </div>
              )}
              {selected.admin_note && (
                <div className="bg-[#0d1117] rounded-lg p-3">
                  <p className="text-[#8b949e] text-xs uppercase tracking-wide mb-1">Заметка администратора</p>
                  <p className="text-white text-sm">{selected.admin_note}</p>
                </div>
              )}
              {selected.status === 'pending' && (
                <>
                  <div>
                    <label className="text-[#8b949e] text-xs uppercase tracking-wide block mb-1">Заметка (необязательно)</label>
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Внутренний комментарий"
                      className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6e7681] focus:outline-none focus:border-[#ff6b00]"
                    />
                  </div>
                  <p className="text-xs text-[#6e7681]">
                    Действие создаст запрос; финальное решение примет
                    второй админ в табе «🕒 Approvals» (4-eyes).
                  </p>
                  <div className="flex gap-3">
                    <Button onClick={handleApprove} disabled={actionLoading} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Запросить одобрение
                    </Button>
                    <Button onClick={handleReject} disabled={actionLoading} variant="outline" className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10">
                      <XCircle className="w-4 h-4 mr-2" />
                      Запросить отклонение
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TotpPromptDialog
        open={!!pendingKind}
        title={
          pendingKind === 'approve'
            ? 'Запросить одобрение возврата'
            : 'Запросить отклонение возврата'
        }
        description={`Refund #${selected?.id ?? ''} · введите ваш 2FA-код. Второй админ подтвердит в табе «Approvals».`}
        confirmLabel="Создать запрос"
        destructive={pendingKind === 'reject'}
        loading={actionLoading}
        onClose={() => setPendingKind(null)}
        onSubmit={submitAction}
      />
    </>
  );
}

// ── Expandable transaction row ───────────────────────────────────────────────
//
// Default — короткая строка с типом/датой/суммой/статусом + кнопка возврата.
// Клик → fetch /admin/transactions/{id} → разворачивается timeline FSM +
// payment provider details + xpanda embed.

interface TxRowProps {
  tx: AdminTx;
  onRequestRefund: (txId: number) => void;
}

function CopyableId({ value, short = 8 }: { value: string; short?: number }) {
  const [copied, setCopied] = useState(false);
  const truncated = value.length > short * 2 + 3
    ? `${value.slice(0, short)}…${value.slice(-short)}`
    : value;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      title={value + ' (клик чтобы скопировать)'}
      className="font-mono text-xs text-[#8b949e] hover:text-white inline-flex items-center gap-1 group"
    >
      {truncated}
      <Copy className={`w-3 h-3 transition-opacity ${copied ? 'opacity-100 text-green-400' : 'opacity-40 group-hover:opacity-100'}`} />
    </button>
  );
}

function TxRow({ tx, onRequestRefund }: TxRowProps) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<TxDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRefund = (tx.type === 'purchase' || tx.type === 'purchase_b2c')
    && (tx.status === 'completed' || tx.status === 'paid');

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      setError(null);
      try {
        const d = await adminApi.getTransactionDetail(String(tx.id));
        setDetail(d);
      } catch (e: any) {
        setError(e.message || 'unknown');
      } finally {
        setLoading(false);
      }
    }
  };

  const typeLabel = TX_TYPE_LABEL[tx.type] || tx.type;
  const statusLabel = TX_STATUS_LABEL[tx.status] || tx.status;

  return (
    <div className="border-b border-[#1a2332] last:border-0">
      {/* Header — clickable */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-[#1a2332]/40 transition-colors px-1 -mx-1 rounded"
      >
        {open ? (
          <ChevronUp className="w-4 h-4 text-[#8b949e] shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#8b949e] shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm truncate">
            {tx.item_name || typeLabel}
          </p>
          <p className="text-[#8b949e] text-xs">
            {fmtDateTime(tx.created_at)} · {typeLabel}
            {tx.payment_method && ` · ${METHOD_LABEL[tx.payment_method] || tx.payment_method}`}
          </p>
        </div>
        <span className={`text-sm font-semibold shrink-0 ${TX_STATUS_COLOR[tx.status] || 'text-white'}`}>
          {tx.amount} ₽
        </span>
        <span className={`text-xs shrink-0 ${TX_STATUS_COLOR[tx.status] || 'text-[#8b949e]'}`}>
          {statusLabel}
        </span>
        {canRefund && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onRequestRefund(tx.id);
            }}
            className="shrink-0 text-xs text-[#ff6b00] hover:underline flex items-center gap-1 cursor-pointer"
          >
            <ReceiptText className="w-3 h-3" />
            Возврат
          </span>
        )}
      </button>

      {/* Expanded detail */}
      {open && (
        <div className="ml-6 mr-1 mb-3 pl-4 border-l-2 border-[#30363d] space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-[#8b949e] py-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Загружаем детали…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-xs text-red-400 py-2">
              <AlertTriangle className="w-3 h-3" />
              {error}
            </div>
          )}
          {detail && (
            <>
              {/* Identity */}
              <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-xs pt-2">
                <span className="text-[#6e7681]">Transaction ID</span>
                <CopyableId value={detail.transaction_id} />

                <span className="text-[#6e7681]">Wallet ID</span>
                <CopyableId value={detail.wallet_id} />

                <span className="text-[#6e7681]">Сумма</span>
                <span className="text-white">
                  {detail.amount} {detail.currency}{' '}
                  <span className="text-[#6e7681]">({detail.amount_minor} {detail.currency} minor)</span>
                </span>

                <span className="text-[#6e7681]">Создана</span>
                <span className="text-[#c9d1d9]">{fmtDateTime(detail.created_at)}</span>

                {detail.completed_at && (
                  <>
                    <span className="text-[#6e7681]">Завершена</span>
                    <span className="text-[#c9d1d9]">{fmtDateTime(detail.completed_at)}</span>
                  </>
                )}
              </div>

              {/* Payment provider */}
              {(detail.payment_provider || detail.payment_provider_id) && (
                <div className="bg-[#0d1117] border border-[#30363d] rounded-lg p-3">
                  <p className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5 text-[#ff6b00]" />
                    Платёжный провайдер
                  </p>
                  <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-xs">
                    {detail.payment_provider && (
                      <>
                        <span className="text-[#6e7681]">Провайдер</span>
                        <span className="text-[#c9d1d9]">{detail.payment_provider}</span>
                      </>
                    )}
                    {detail.payment_method && (
                      <>
                        <span className="text-[#6e7681]">Метод</span>
                        <span className="text-[#c9d1d9]">{METHOD_LABEL[detail.payment_method] || detail.payment_method}</span>
                      </>
                    )}
                    {detail.payment_provider_id && (
                      <>
                        <span className="text-[#6e7681]">Provider ID</span>
                        <CopyableId value={detail.payment_provider_id} short={10} />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Purchase embed (Xpanda info) */}
              {detail.purchase && (
                <div className="bg-[#0d1117] border border-[#30363d] rounded-lg p-3">
                  <p className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-[#ff6b00]" />
                    Покупка (Xpanda P2P)
                  </p>
                  <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-xs">
                    <span className="text-[#6e7681]">Purchase ID</span>
                    <CopyableId value={detail.purchase.purchase_id} />

                    <span className="text-[#6e7681]">Product</span>
                    <span className="text-[#c9d1d9] font-mono break-all">{detail.purchase.product_id}</span>

                    <span className="text-[#6e7681]">Purchase status</span>
                    <span className="text-[#c9d1d9]">
                      {PURCHASE_STATUS_LABEL[detail.purchase.status] || detail.purchase.status}
                    </span>

                    {detail.purchase.xpanda_deal_id && (
                      <>
                        <span className="text-[#6e7681]">Xpanda deal ID</span>
                        <CopyableId value={detail.purchase.xpanda_deal_id} short={10} />
                      </>
                    )}
                    {detail.purchase.xpanda_custom_id && (
                      <>
                        <span className="text-[#6e7681]">Custom ID</span>
                        <CopyableId value={detail.purchase.xpanda_custom_id} short={10} />
                      </>
                    )}
                    {detail.purchase.trade_offer_id && (
                      <>
                        <span className="text-[#6e7681]">Trade offer</span>
                        <a
                          href={`https://steamcommunity.com/tradeoffer/${detail.purchase.trade_offer_id}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#ff6b00] hover:underline inline-flex items-center gap-1"
                        >
                          #{detail.purchase.trade_offer_id}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </>
                    )}
                    {detail.purchase.delivered_at && (
                      <>
                        <span className="text-[#6e7681]">Доставлено</span>
                        <span className="text-green-400">{fmtDateTime(detail.purchase.delivered_at)}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* State history timeline */}
              <div className="bg-[#0d1117] border border-[#30363d] rounded-lg p-3">
                <p className="text-xs font-semibold text-white mb-3 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5 text-[#ff6b00]" />
                  История состояний ({detail.state_history.length})
                </p>
                {detail.state_history.length === 0 ? (
                  <p className="text-xs text-[#6e7681]">История пуста</p>
                ) : (
                  <ol className="relative space-y-3">
                    {detail.state_history.map((step, i) => (
                      <li key={i} className="pl-5 relative">
                        <span className="absolute left-0 top-1 w-2.5 h-2.5 rounded-full bg-[#ff6b00] ring-4 ring-[#0d1117]" />
                        {i < detail.state_history.length - 1 && (
                          <span className="absolute left-[5px] top-3.5 bottom-[-12px] w-px bg-[#30363d]" />
                        )}
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          {step.from_status && (
                            <>
                              <span className="text-[#6e7681]">{TX_STATUS_LABEL[step.from_status] || step.from_status}</span>
                              <span className="text-[#6e7681]">→</span>
                            </>
                          )}
                          <span className={`font-semibold ${TX_STATUS_COLOR[step.to_status] || 'text-[#c9d1d9]'}`}>
                            {TX_STATUS_LABEL[step.to_status] || step.to_status}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#6e7681]">
                          <span>{fmtDateTime(step.occurred_at)}</span>
                          {step.actor && <span>· actor: <span className="text-[#8b949e]">{step.actor}</span></span>}
                          {step.reason && <span>· {step.reason}</span>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ── Orders tab ────────────────────────────────────────────────────────────────

interface OrderTx extends AdminTx {
  wallet_id?: string;
  user?: { id: number; steam_name: string; steam_avatar: string | null } | null;
}

function OrdersTab() {
  const [items, setItems] = useState<OrderTx[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // page_token-стек: [0] — текущая страница, добавляем при «вперёд», pop при «назад»
  const [tokenStack, setTokenStack] = useState<(string | null)[]>([null]);
  const [nextToken, setNextToken] = useState<string | null>(null);

  // График
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return isoDate(d); });
  const [dateTo, setDateTo]     = useState(() => isoDate(new Date()));

  const chartData = useMemo(
    () => buildChartSlots(items, dateFrom || '2020-01-01', dateTo || isoDate(new Date()), granularity),
    [items, dateFrom, dateTo, granularity],
  );

  const applyOrderDatePreset = (days: number) => {
    const now = new Date();
    const from = days === 0 ? new Date('2020-01-01') : (() => { const d = new Date(now); d.setDate(d.getDate() - days + 1); return d; })();
    setDateFrom(isoDate(from));
    setDateTo(isoDate(now));
  };

  // refund dialog (переиспользуем логику из UsersTab)
  const [refundTxId, setRefundTxId] = useState<number | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  const load = async (pageToken: string | null = null) => {
    setLoading(true);
    try {
      const data = await adminApi.listTransactions({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        pageToken: pageToken ?? undefined,
        limit: 50,
      });
      setItems((data as any).items ?? []);
      setNextToken((data as any).next_page_token ?? null);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  // Загружаем при монтировании и при смене фильтров (сброс на первую страницу)
  useEffect(() => {
    setTokenStack([null]);
    void load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  const handleNext = () => {
    if (!nextToken) return;
    const newStack = [...tokenStack, nextToken];
    setTokenStack(newStack);
    void load(nextToken);
  };

  const handlePrev = () => {
    if (tokenStack.length <= 1) return;
    const newStack = tokenStack.slice(0, -1);
    setTokenStack(newStack);
    void load(newStack[newStack.length - 1]);
  };

  const handleCreateRefund = async () => {
    if (!refundTxId) return;
    setRefundLoading(true);
    try {
      await adminApi.createRefundRequest(refundTxId, refundReason || 'Создано администратором');
      toast.success('Заявка на возврат создана');
      setRefundTxId(null);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка');
    } finally {
      setRefundLoading(false);
    }
  };

  const totalAmount = items.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div>
      {/* Фильтры */}
      <div className="flex gap-3 flex-wrap items-center mb-5">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-[#161b22] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff6b00]"
        >
          <option value="">Все статусы</option>
          {Object.entries(TX_STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="bg-[#161b22] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff6b00]"
        >
          <option value="">Все типы</option>
          {Object.entries(TX_TYPE_LABEL).filter(([k]) => k !== 'purchase_b2c').map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button
          onClick={() => { setTokenStack([null]); void load(null); }}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#161b22] border border-[#30363d] text-[#8b949e] hover:text-white text-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </button>
        {items.length > 0 && (
          <span className="text-xs text-[#6e7681] ml-auto">
            {items.length} {pluralize(items.length, ['заказ', 'заказа', 'заказов'])}
            {' · '}
            <span className="text-[#ff6b00] font-semibold">{totalAmount.toLocaleString('ru-RU')} ₽</span>
          </span>
        )}
      </div>

      {/* График */}
      <div className="mb-4">
        <div className="flex gap-2 flex-wrap items-center mb-3">
          <span className="text-xs text-[#6e7681]">Период:</span>
          {([['День', 1], ['Неделя', 7], ['Месяц', 30], ['3 мес', 90], ['Всё время', 0]] as [string, number][]).map(([label, days]) => {
            const active = days === 0
              ? new Date(dateFrom) <= new Date('2021-01-01')
              : Math.round((new Date(dateTo).getTime() - new Date(dateFrom).getTime()) / 86_400_000) === days - 1;
            return (
              <button key={days} onClick={() => applyOrderDatePreset(days)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-[#ff6b00] border-[#ff6b00] text-white' : 'border-[#30363d] bg-[#161b22] text-[#8b949e] hover:text-white hover:border-[#ff6b00]/50'}`}>
                {label}
              </button>
            );
          })}
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="bg-[#161b22] border border-[#30363d] rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#ff6b00]" />
          <span className="text-[#6e7681] text-xs">—</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="bg-[#161b22] border border-[#30363d] rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#ff6b00]" />
        </div>
        <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#8b949e]">Транзакции по {GRAN_TITLE[granularity]}</p>
            <div className="flex gap-1">
              {(Object.keys(GRAN_LABELS) as Granularity[]).map(g => (
                <button key={g} onClick={() => setGranularity(g)}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${granularity === g ? 'bg-[#ff6b00] border-[#ff6b00] text-white' : 'border-[#30363d] text-[#6e7681] hover:text-white'}`}>
                  {GRAN_LABELS[g]}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: '#6e7681', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: '#6e7681', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', color: '#c9d1d9', fontSize: '12px' }} labelStyle={{ color: '#8b949e' }} itemStyle={{ color: '#ff6b00' }} cursor={{ fill: '#1a2332' }} />
              <Bar dataKey="count" name="Транзакции" fill="#ff6b00" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Список */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff6b00]" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#8b949e] py-12">Транзакций не найдено</p>
      ) : (
        <div className="space-y-2">
          {items.map(tx => (
            <div key={String(tx.id)} className="bg-[#161b22] border border-[#30363d] rounded-xl px-4 pt-3 pb-1">
              {/* Пользователь */}
              {tx.user ? (
                <div className="flex items-center gap-2 mb-2">
                  {tx.user.steam_avatar ? (
                    <img src={tx.user.steam_avatar} alt={tx.user.steam_name}
                      className="w-5 h-5 rounded-full border border-[#30363d]" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#1a2332]" />
                  )}
                  <span className="text-xs text-[#8b949e]">{tx.user.steam_name}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-[#1a2332]" />
                  <span className="text-xs text-[#484f58] font-mono">{tx.wallet_id?.slice(0, 12)}…</span>
                </div>
              )}
              <TxRow tx={tx} onRequestRefund={id => { setRefundTxId(id); setRefundReason(''); }} />
            </div>
          ))}
        </div>
      )}

      {/* Пагинация */}
      {(tokenStack.length > 1 || nextToken) && (
        <div className="flex justify-between mt-6">
          <button
            onClick={handlePrev}
            disabled={tokenStack.length <= 1 || loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#161b22] border border-[#30363d] text-sm text-[#8b949e] hover:text-white disabled:opacity-40 transition-colors"
          >
            ← Назад
          </button>
          <button
            onClick={handleNext}
            disabled={!nextToken || loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#161b22] border border-[#30363d] text-sm text-[#8b949e] hover:text-white disabled:opacity-40 transition-colors"
          >
            Вперёд →
          </button>
        </div>
      )}

      {/* Refund dialog */}
      <Dialog open={!!refundTxId} onOpenChange={open => { if (!open) setRefundTxId(null); }}>
        <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Создать заявку на возврат</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-[#8b949e] text-sm">Транзакция #{refundTxId}</p>
            <input
              value={refundReason}
              onChange={e => setRefundReason(e.target.value)}
              placeholder="Создано администратором"
              className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6e7681] focus:outline-none focus:border-[#ff6b00]"
            />
            <Button onClick={handleCreateRefund} disabled={refundLoading}
              className="w-full bg-[#ff6b00] hover:bg-[#ff8c00] text-white">
              Создать заявку
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Shared chart helpers ──────────────────────────────────────────────────────

type Granularity = 'hour' | 'day' | 'week' | 'month';
const GRAN_LABELS: Record<Granularity, string> = { hour: '1ч', day: '1д', week: '1нед', month: '1мес' };
const GRAN_TITLE: Record<Granularity, string> = { hour: 'часам', day: 'дням', week: 'неделям', month: 'месяцам' };

function getSlotKey(dateStr: string, g: Granularity): string {
  if (g === 'hour')  return dateStr.slice(0, 13); // "YYYY-MM-DDTHH"
  if (g === 'day')   return dateStr.slice(0, 10); // "YYYY-MM-DD"
  if (g === 'month') return dateStr.slice(0, 7);  // "YYYY-MM"
  // week → Monday of the week
  const d = new Date(dateStr.slice(0, 10));
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d.toISOString().slice(0, 10);
}

function buildChartSlots(
  items: { created_at?: string | null }[],
  dateFrom: string,
  dateTo: string,
  g: Granularity,
): { date: string; count: number }[] {
  const counts: Record<string, number> = {};
  for (const item of items) {
    if (!item.created_at) continue;
    const key = getSlotKey(item.created_at, g);
    counts[key] = (counts[key] || 0) + 1;
  }

  const slots: { date: string; count: number }[] = [];
  if (g === 'hour') {
    const cur = new Date(dateFrom + 'T00:00:00');
    const end = new Date(dateTo + 'T23:00:00');
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 13);
      slots.push({ date: key.slice(11, 13) + ':00', count: counts[key] || 0 });
      cur.setHours(cur.getHours() + 1);
    }
  } else if (g === 'day') {
    const cur = new Date(dateFrom);
    const end = new Date(dateTo);
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 10);
      slots.push({ date: key.slice(5), count: counts[key] || 0 });
      cur.setDate(cur.getDate() + 1);
    }
  } else if (g === 'week') {
    const start = new Date(dateFrom);
    const dow = start.getDay();
    start.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1));
    const end = new Date(dateTo);
    const cur = new Date(start);
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 10);
      slots.push({ date: key.slice(5), count: counts[key] || 0 });
      cur.setDate(cur.getDate() + 7);
    }
  } else {
    const [fy, fm] = dateFrom.split('-').map(Number);
    const [ty, tm] = dateTo.split('-').map(Number);
    let y = fy, m = fm;
    while (y < ty || (y === ty && m <= tm)) {
      const key = `${y}-${String(m).padStart(2, '0')}`;
      slots.push({ date: key, count: counts[key] || 0 });
      m++; if (m > 12) { m = 1; y++; }
    }
  }
  return slots;
}

// ── Users tab ─────────────────────────────────────────────────────────────────

interface UserFilters {
  adminsOnly: boolean;
  balanceGtZero: boolean;
  active7d: boolean;
  new24h: boolean;
  hasTradeUrl: boolean;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: UserFilters = {
  adminsOnly: false,
  balanceGtZero: false,
  active7d: false,
  new24h: false,
  hasTradeUrl: false,
  dateFrom: '',
  dateTo: '',
};

function isoDate(d: Date) { return d.toISOString().slice(0, 10); }

function defaultMonthFilters(): UserFilters {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 29);
  return { ...EMPTY_FILTERS, dateFrom: isoDate(from), dateTo: isoDate(now) };
}

function UsersTab() {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<UserFilters>(defaultMonthFilters);
  const [results, setResults] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // balance adjustment
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceNote, setBalanceNote] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(false);

  // refund creation
  const [refundTxId, setRefundTxId] = useState<number | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  const [granularity, setGranularity] = useState<Granularity>('day');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Один fetcher для load + поиск + фильтры. Search дебаунсится на 300ms,
  // toggles фильтров запрашивают сразу.
  const fetchUsers = async (q: string, f: UserFilters) => {
    setLoading(true);
    try {
      const isAllTime = !!f.dateFrom && f.dateFrom <= '2021-01-01';
      const data = await adminApi.listUsers({
        q: q.trim() || undefined,
        adminsOnly: f.adminsOnly,
        balanceGtZero: f.balanceGtZero,
        active7d: f.active7d,
        new24h: f.new24h,
        hasTradeUrl: f.hasTradeUrl,
        createdAfter: f.dateFrom || undefined,
        createdBefore: f.dateTo || undefined,
        limit: isAllTime ? 2000 : 200,
      });
      setResults(data);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  // Initial load + при смене фильтров.
  useEffect(() => {
    void fetchUsers(query, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleSearch = (v: string) => {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchUsers(v, filters);
    }, 300);
  };

  const toggleFilter = (key: keyof UserFilters) =>
    setFilters((f) => ({ ...f, [key]: !f[key] }));

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setQuery('');
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  // Агрегируем created_at по выбранной гранулярности, включая пустые слоты
  const chartData = useMemo(() => {
    const from = filters.dateFrom || '2020-01-01';
    const to   = filters.dateTo   || isoDate(new Date());
    if (!filters.dateFrom && !filters.dateTo) return [];
    return buildChartSlots(results, from, to, granularity);
  }, [results, filters.dateFrom, filters.dateTo, granularity]);

  const applyDatePreset = (days: number) => {
    const now = new Date();
    const from = days === 0 ? new Date('2020-01-01') : (() => {
      const d = new Date(now); d.setDate(d.getDate() - days + 1); return d;
    })();
    setFilters(f => ({ ...f, dateFrom: isoDate(from), dateTo: isoDate(now) }));
  };

  const openDetail = async (userId: number) => {
    setLoadingDetail(true);
    setDetail(null);
    setBalanceAmount('');
    setBalanceNote('');
    try {
      const data = await adminApi.getUserDetail(userId);
      setDetail(data);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка');
    } finally {
      setLoadingDetail(false);
    }
  };

  // 4-eyes: вместо direct adjust теперь — создаём pending action,
  // второй админ апрувит в табе «Approvals».
  const [pendingAdjust, setPendingAdjust] = useState<{ sign: 1 | -1; amount: number } | null>(null);

  const submitAdjustAction = async (totpCode: string) => {
    if (!detail || !pendingAdjust) return;
    if (!detail.wallet_id) {
      toast.error('У этого пользователя нет wallet_id (новый аккаунт?)');
      setPendingAdjust(null);
      return;
    }
    setBalanceLoading(true);
    try {
      // delta_minor — копейки. UI принимает в рублях.
      const deltaMinor = pendingAdjust.amount * pendingAdjust.sign * 100;
      await adminApi.createAction(
        'wallet_adjust',
        {
          wallet_id: detail.wallet_id,
          delta_minor: deltaMinor,
          reason: balanceNote || 'admin balance adjust',
        },
        totpCode,
      );
      toast.success(
        `Запрос на ${pendingAdjust.sign > 0 ? 'пополнение' : 'списание'} ` +
        `${pendingAdjust.amount} ₽ создан — нужен второй админ`,
      );
      setBalanceAmount('');
      setBalanceNote('');
      setPendingAdjust(null);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка');
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleAdjustBalance = (sign: 1 | -1) => {
    if (!detail || !balanceAmount) return;
    const amount = parseInt(balanceAmount);
    if (isNaN(amount) || amount === 0) return;
    setPendingAdjust({ sign, amount });
  };

  const handleCreateRefund = async () => {
    if (!refundTxId) return;
    setRefundLoading(true);
    try {
      await adminApi.createRefundRequest(refundTxId, refundReason || undefined);
      toast.success('Заявка на возврат создана');
      setRefundTxId(null);
      setRefundReason('');
      if (detail) {
        const updated = await adminApi.getUserDetail(detail.id);
        setDetail(updated);
      }
    } catch (e: any) {
      toast.error(e.message || 'Ошибка');
    } finally {
      setRefundLoading(false);
    }
  };

  if (detail) {
    return (
      <div>
        <button
          onClick={() => setDetail(null)}
          className="flex items-center gap-2 text-[#8b949e] hover:text-white mb-5 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Назад к поиску
        </button>

        {/* User info */}
        <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-4 flex items-center gap-4 mb-4">
          {detail.steam_avatar ? (
            <img src={detail.steam_avatar} alt={detail.steam_name} className="w-14 h-14 rounded-full border border-[#30363d]" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#1a2332] flex items-center justify-center text-[#8b949e] text-xl">?</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-lg truncate">{detail.steam_name}</p>
            <p className="text-[#8b949e] text-sm">{detail.email || '—'} · {detail.phone || '—'}</p>
            <p className="text-[#8b949e] text-xs">ID: {detail.id} · Steam: {detail.steam_id}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[#ff6b00] font-bold text-xl">
              {detail.balance != null ? `${detail.balance} ₽` : '—'}
            </p>
            <p className="text-[#6e7681] text-xs">Зарегистрирован {fmtDate(detail.created_at)}</p>
          </div>
        </div>

        {/* Balance adjustment */}
        <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-4 mb-4">
          <p className="text-white font-semibold mb-3">Управление балансом</p>
          <div className="flex gap-2 flex-wrap">
            <input
              type="number"
              min="1"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
              placeholder="Сумма (₽)"
              className="flex-1 min-w-[120px] bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6e7681] focus:outline-none focus:border-[#ff6b00]"
            />
            <input
              value={balanceNote}
              onChange={(e) => setBalanceNote(e.target.value)}
              placeholder="Причина (необязательно)"
              className="flex-1 min-w-[160px] bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6e7681] focus:outline-none focus:border-[#ff6b00]"
            />
            <Button
              onClick={() => handleAdjustBalance(1)}
              disabled={balanceLoading || !balanceAmount}
              className="bg-green-600 hover:bg-green-700 text-white shrink-0"
            >
              <Plus className="w-4 h-4 mr-1" />
              Запросить +
            </Button>
            <Button
              onClick={() => handleAdjustBalance(-1)}
              disabled={balanceLoading || !balanceAmount}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 shrink-0"
            >
              <Minus className="w-4 h-4 mr-1" />
              Запросить −
            </Button>
          </div>
          <p className="text-xs text-[#6e7681] mt-2">
            Создаёт запрос с 2FA. Второй админ подтвердит в табе «🕒 Approvals» (4-eyes).
          </p>
        </div>

        {/* Transactions + create refund */}
        <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white font-semibold">Последние транзакции</p>
            <p className="text-xs text-[#6e7681]">Клик по строке — полный лог</p>
          </div>
          {detail.transactions.length === 0 ? (
            <p className="text-[#8b949e] text-sm">Транзакций нет</p>
          ) : (
            <div>
              {detail.transactions.map((tx) => (
                <TxRow
                  key={tx.id}
                  tx={tx}
                  onRequestRefund={(txId) => { setRefundTxId(txId); setRefundReason(''); }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Create refund dialog */}
        <Dialog open={!!refundTxId} onOpenChange={(open) => { if (!open) setRefundTxId(null); }}>
          <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Создать заявку на возврат</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-[#8b949e] text-sm">Транзакция #{refundTxId}</p>
              <div>
                <label className="text-[#8b949e] text-xs uppercase tracking-wide block mb-1">Причина</label>
                <input
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Создано администратором"
                  className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6e7681] focus:outline-none focus:border-[#ff6b00]"
                />
              </div>
              <Button onClick={handleCreateRefund} disabled={refundLoading} className="w-full bg-[#ff6b00] hover:bg-[#ff8c00] text-white">
                Создать заявку
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <TotpPromptDialog
          open={!!pendingAdjust}
          title={
            pendingAdjust?.sign === 1
              ? `Пополнить баланс +${pendingAdjust?.amount} ₽`
              : `Списать с баланса −${pendingAdjust?.amount} ₽`
          }
          description={`Пользователь ${detail.steam_name} (id=${detail.id}) · введите ваш 2FA-код. Второй админ подтвердит в табе «Approvals».`}
          confirmLabel="Создать запрос"
          destructive={pendingAdjust?.sign === -1}
          loading={balanceLoading}
          onClose={() => setPendingAdjust(null)}
          onSubmit={submitAdjustAction}
        />
      </div>
    );
  }

  const filterChips: { key: keyof UserFilters; label: string }[] = [
    { key: 'adminsOnly', label: 'Только админы' },
    { key: 'balanceGtZero', label: 'Баланс > 0' },
    { key: 'active7d', label: 'Активные 7д' },
    { key: 'new24h', label: 'Новые 24ч' },
    { key: 'hasTradeUrl', label: 'С trade-url' },
  ];

  return (
    <div>
      {/* Search input */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8b949e]" />
        <input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Поиск по nickname, Steam ID или email…"
          className="w-full bg-[#161b22] border border-[#30363d] rounded-xl pl-10 pr-4 py-3 text-white placeholder-[#6e7681] focus:outline-none focus:border-[#ff6b00] transition-colors"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#ff6b00]" />
          </div>
        )}
      </div>

      {/* Date presets */}
      <div className="flex gap-2 flex-wrap items-center mb-3">
        <span className="text-xs text-[#6e7681]">Период:</span>
        {([['День', 1], ['Неделя', 7], ['Месяц', 30], ['3 мес', 90], ['Всё время', 0]] as [string, number][]).map(([label, days]) => {
          const active = (() => {
            if (!filters.dateFrom || !filters.dateTo) return false;
            if (days === 0) return new Date(filters.dateFrom) <= new Date('2021-01-01');
            const diff = Math.round((new Date(filters.dateTo).getTime() - new Date(filters.dateFrom).getTime()) / 86_400_000);
            return diff === days - 1;
          })();
          return (
            <button
              key={days}
              onClick={() => applyDatePreset(days)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'bg-[#ff6b00] border-[#ff6b00] text-white'
                  : 'border-[#30363d] bg-[#161b22] text-[#8b949e] hover:text-white hover:border-[#ff6b00]/50'
              }`}
            >
              {label}
            </button>
          );
        })}
        <input
          type="date"
          value={filters.dateFrom}
          onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
          className="bg-[#161b22] border border-[#30363d] rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#ff6b00]"
        />
        <span className="text-[#6e7681] text-xs">—</span>
        <input
          type="date"
          value={filters.dateTo}
          onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
          className="bg-[#161b22] border border-[#30363d] rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#ff6b00]"
        />
        {(filters.dateFrom || filters.dateTo) && (
          <button
            onClick={() => setFilters(f => ({ ...f, dateFrom: '', dateTo: '' }))}
            className="text-xs text-[#8b949e] hover:text-white px-2"
          >
            ✕
          </button>
        )}
      </div>

      {/* Bar chart */}
      {(filters.dateFrom || filters.dateTo) && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#8b949e]">Новые пользователи по {GRAN_TITLE[granularity]}</p>
            <div className="flex gap-1">
              {(Object.keys(GRAN_LABELS) as Granularity[]).map(g => (
                <button key={g} onClick={() => setGranularity(g)}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                    granularity === g
                      ? 'bg-[#ff6b00] border-[#ff6b00] text-white'
                      : 'border-[#30363d] text-[#6e7681] hover:text-white'
                  }`}>{GRAN_LABELS[g]}</button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: '#6e7681', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: '#6e7681', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', color: '#c9d1d9', fontSize: '12px' }}
                labelStyle={{ color: '#8b949e' }}
                itemStyle={{ color: '#ff6b00' }}
                cursor={{ fill: '#1a2332' }}
              />
              <Bar dataKey="count" name="Пользователи" fill="#ff6b00" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap items-center mb-5">
        {filterChips.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => toggleFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filters[key as keyof UserFilters]
                ? 'bg-[#ff6b00] border-[#ff6b00] text-white'
                : 'bg-[#161b22] border-[#30363d] text-[#8b949e] hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
        {(activeFiltersCount > 0 || query) && (
          <button
            onClick={resetFilters}
            className="text-xs text-[#8b949e] hover:text-white px-2 py-1"
          >
            сбросить
          </button>
        )}
        <span className="text-xs text-[#6e7681] ml-auto">
          {results.length} {pluralize(results.length, ['результат', 'результата', 'результатов'])}
        </span>
      </div>

      {results.length === 0 && !loading && (
        <p className="text-center text-[#8b949e] py-8">
          Ничего не найдено по фильтрам
        </p>
      )}

      <div className="space-y-2">
        {results.map((u) => (
          <button
            key={u.id}
            onClick={() => openDetail(u.id)}
            className="w-full bg-[#161b22] border border-[#30363d] rounded-xl p-4 flex items-center gap-4 hover:border-[#ff6b00]/40 transition-colors text-left"
          >
            {u.steam_avatar ? (
              <img src={u.steam_avatar} alt={u.steam_name} className="w-10 h-10 rounded-full border border-[#30363d] shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#1a2332] shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{u.steam_name}</p>
              <p className="text-[#8b949e] text-xs truncate">{u.email || '—'}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[#ff6b00] font-semibold">
                {u.balance != null ? `${u.balance} ₽` : '—'}
              </p>
              {u.is_admin && <span className="text-xs text-yellow-400">admin</span>}
            </div>
            <ChevronRight className="w-4 h-4 text-[#8b949e] shrink-0" />
          </button>
        ))}
      </div>

      {loadingDetail && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ff6b00]" />
        </div>
      )}
    </div>
  );
}

function pluralize(n: number, forms: [string, string, string]): string {
  // RU plural: 1 / 2-4 / 5+, с учётом -teen.
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** Бэкенд отдаёт naive UTC (datetime.utcnow().isoformat()) без суффикса —
 * без 'Z' JS парсит datetime-строку как ЛОКАЛЬНОЕ время. Дописываем 'Z',
 * если в строке нет ни Z, ни явного offset'а. */
function parseUtc(iso: string): Date {
  return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return parseUtc(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** «2 часа назад» — для last_activity. Возвращает '—' для пустого. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const sec = Math.floor((Date.now() - parseUtc(iso).getTime()) / 1000);
  if (sec < 0) return 'только что';
  if (sec < 60) return 'только что';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} ${pluralize(min, ['минуту', 'минуты', 'минут'])} назад`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} ${pluralize(hr, ['час', 'часа', 'часов'])} назад`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} ${pluralize(days, ['день', 'дня', 'дней'])} назад`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${pluralize(months, ['месяц', 'месяца', 'месяцев'])} назад`;
  const years = Math.floor(months / 12);
  return `${years} ${pluralize(years, ['год', 'года', 'лет'])} назад`;
}

/** Инициалы для аватар-фоллбэка: до 2 символов из ника. */
function initialsFrom(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// ── Audit log tab ─────────────────────────────────────────────────────────────

function AuditTab() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchPage = async (afterId: number) => {
    const data = (await adminApi.listAudit(afterId, 50)) as AuditLogPage;
    return data;
  };

  const load = async () => {
    setLoading(true);
    setEntries([]);
    setNextToken(null);
    try {
      const data = await fetchPage(0);
      setEntries(data.items);
      setNextToken(data.next_page_token);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка загрузки audit-лога');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(Number(nextToken));
      setEntries((prev) => [...prev, ...data.items]);
      setNextToken(data.next_page_token);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка загрузки');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#8b949e]">
          Append-only hash-chain. Каждая запись содержит prev_hash → SHA-256
          цепочка верифицируется отдельным скриптом.
        </div>
        <Button variant="ghost" className="text-[#8b949e]" onClick={load}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ff6b00]" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-[#8b949e]">Записей нет</div>
      ) : (
        <>
          <div className="bg-[#161b22] border border-[#30363d] rounded-xl overflow-hidden">
            <div className="grid grid-cols-[80px_1fr_180px_140px_40px] gap-3 px-4 py-3 text-xs uppercase tracking-wider text-[#8b949e] border-b border-[#30363d]">
              <div>ID</div>
              <div>Action / Subject</div>
              <div>Hash (prefix)</div>
              <div>Когда</div>
              <div></div>
            </div>
            {entries.map((e) => {
              const action = String(e.payload?.action ?? e.payload?.event ?? '—');
              const subject = String(e.payload?.subject ?? e.payload?.target ?? '');
              const ts = e.payload?.timestamp ?? e.payload?.occurred_at ?? '';
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} className="border-b border-[#30363d] last:border-b-0">
                  <button
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                    className="w-full grid grid-cols-[80px_1fr_180px_140px_40px] gap-3 px-4 py-3 text-sm text-left hover:bg-[#21262d] transition-colors"
                  >
                    <div className="font-mono text-[#8b949e]">{e.id}</div>
                    <div className="truncate">
                      <span className="text-white font-medium">{action}</span>
                      {subject && <span className="text-[#8b949e] ml-2">{subject}</span>}
                    </div>
                    <div className="font-mono text-xs text-[#8b949e]">{e.hash.slice(0, 16)}…</div>
                    <div className="text-xs text-[#8b949e]">{typeof ts === 'string' ? ts.slice(0, 19).replace('T', ' ') : ''}</div>
                    <ChevronRight className={`w-4 h-4 text-[#8b949e] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 py-3 bg-[#0d1117] border-t border-[#30363d]">
                      <div className="text-xs text-[#8b949e] mb-2">prev_hash: <span className="font-mono text-white">{e.prev_hash}</span></div>
                      <div className="text-xs text-[#8b949e] mb-2">hash: <span className="font-mono text-white">{e.hash}</span></div>
                      <pre className="text-xs text-[#c9d1d9] font-mono whitespace-pre-wrap break-all bg-[#161b22] p-3 rounded border border-[#30363d] overflow-x-auto">
{JSON.stringify(e.payload, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {nextToken && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                className="bg-[#161b22] border-[#30363d] text-white hover:bg-[#21262d]"
              >
                {loadingMore ? 'Загрузка...' : 'Загрузить ещё 50'}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Maintenance tab ───────────────────────────────────────────────────────────

function MaintenanceTab() {
  const [state, setState] = useState<MaintenanceState | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = (await adminApi.getMaintenance()) as MaintenanceState;
      setState(s);
      setMessage(s.message);
    } catch (e: any) {
      toast.error(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (newEnabled: boolean) => {
    setSaving(true);
    try {
      const s = (await adminApi.setMaintenance(newEnabled, message)) as MaintenanceState;
      setState(s);
      toast.success(newEnabled ? 'Maintenance mode ВКЛЮЧЁН' : 'Maintenance mode ВЫКЛЮЧЕН');
    } catch (e: any) {
      toast.error(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ff6b00]" />
      </div>
    );
  }

  const enabled = state?.enabled ?? false;

  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-lg font-semibold">Maintenance mode</div>
          <div className="text-sm text-[#8b949e] mt-1">
            Когда включён — сайт доступен <span className="text-white">только админам</span>.
            Пользователи получают 503 с твоим сообщением. Webhooks провайдеров и payments-core
            channel'ы остаются открытыми (allowlist).
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-bold ${
            enabled ? 'bg-red-900 text-red-200' : 'bg-green-900 text-green-200'
          }`}
        >
          {enabled ? 'ON' : 'OFF'}
        </span>
      </div>

      <label className="block text-sm text-[#8b949e] mb-2">Сообщение пользователю</label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Сайт временно недоступен из-за работ по обслуживанию."
        className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg p-3 text-sm text-white min-h-[80px] mb-4"
      />

      <div className="flex gap-3">
        <Button
          onClick={() => toggle(true)}
          disabled={saving || enabled}
          className="bg-red-700 hover:bg-red-600 text-white"
        >
          Включить maintenance
        </Button>
        <Button
          onClick={() => toggle(false)}
          disabled={saving || !enabled}
          className="bg-green-700 hover:bg-green-600 text-white"
        >
          Снять maintenance
        </Button>
      </div>

      <div className="mt-6 text-xs text-[#8b949e]">
        💡 Cache 5s — изменение применится для всех uvicorn workers до 5 секунд.
        Для admin'ов — без задержки (60s admin-cache TTL).
      </div>
    </div>
  );
}


// ── 4-eyes TOTP prompt + Approvals queue ───────────────────────────────────────

interface TotpPromptProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void> | void;
}

function TotpPromptDialog({
  open, title, description, confirmLabel = 'Подтвердить',
  destructive, loading, onClose, onSubmit,
}: TotpPromptProps) {
  const [code, setCode] = useState('');
  useEffect(() => { if (!open) setCode(''); }, [open]);
  const valid = /^\d{6,8}$/.test(code);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#ff6b00]" />
            {title}
          </DialogTitle>
        </DialogHeader>
        {description && (
          <p className="text-sm text-[#8b949e]">{description}</p>
        )}
        <div className="space-y-3 mt-2">
          <div>
            <label className="block text-xs text-[#8b949e] mb-1">
              Код из приложения 2FA
            </label>
            <input
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="123456"
              inputMode="numeric"
              className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm font-mono text-center text-lg tracking-widest focus:outline-none focus:border-[#ff6b00]"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              className="border-[#30363d] text-[#8b949e] hover:bg-[#21262d]"
            >
              Отмена
            </Button>
            <Button
              onClick={async () => { if (valid) await onSubmit(code); }}
              disabled={!valid || loading}
              className={
                destructive
                  ? 'bg-red-700 hover:bg-red-800 text-white'
                  : 'bg-[#ff6b00] hover:bg-[#e55a00] text-white'
              }
            >
              {loading ? 'Отправка…' : confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


const ACTION_TYPE_LABEL: Record<string, string> = {
  refund_approve: 'Одобрить возврат',
  refund_reject: 'Отклонить возврат',
  wallet_adjust: 'Корректировка баланса',
};

const ACTION_STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает подтверждения',
  approved: 'Одобрена',
  executed: 'Выполнена',
  rejected: 'Отклонена',
  expired: 'Истекла',
};

const ACTION_STATUS_COLOR: Record<string, string> = {
  pending: 'text-yellow-400',
  approved: 'text-blue-400',
  executed: 'text-green-400',
  rejected: 'text-red-400',
  expired: 'text-[#8b949e]',
};

function ApprovalsTab() {
  const { user } = useAuth();
  const myAdminId = user?.pc_admin_id || null;
  const [items, setItems] = useState<PendingAction[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<{ kind: 'approve' | 'reject'; item: PendingAction } | null>(null);
  const [busy, setBusy] = useState(false);
  // map admin_id → external_id для UI
  const [admins, setAdmins] = useState<PcAdminItem[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const resp = (await adminApi.listActions(statusFilter || undefined, 100)) as PendingActionsList;
      setItems(resp.items || []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Не удалось загрузить: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const resp = (await adminApi.listAdmins(true)) as PcAdminListResponse;
      setAdmins(resp.items || []);
    } catch {
      // молча — UI всё равно покажет UUID если не получится
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { void loadAdmins(); }, []);

  const adminLabel = (admin_id: string | null | undefined) => {
    if (!admin_id) return '—';
    const a = admins.find((x) => x.admin_id === admin_id);
    return a ? (a.email || a.external_id) : admin_id.slice(0, 8) + '…';
  };

  const submit = async (code: string) => {
    if (!action) return;
    setBusy(true);
    try {
      if (action.kind === 'approve') {
        await adminApi.approveAction(action.item.action_id, code);
        toast.success('Одобрено и выполнено');
      } else {
        await adminApi.rejectAction(action.item.action_id, code);
        toast.success('Отклонено');
      }
      setAction(null);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Ошибка: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-[#161b22] p-1 rounded-xl border border-[#30363d]">
          {(['pending', 'executed', 'rejected', 'expired', ''] as const).map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-[#ff6b00] text-white'
                  : 'text-[#8b949e] hover:text-white'
              }`}
            >
              {s ? (ACTION_STATUS_LABEL[s] || s) : 'Все'}
            </button>
          ))}
        </div>
        <Button
          onClick={() => void load()}
          variant="outline"
          className="border-[#30363d] hover:bg-[#21262d] h-8 text-xs"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {items.length === 0 && !loading && (
        <p className="text-center text-[#8b949e] py-8">Действий нет</p>
      )}

      <div className="space-y-2">
        {items.map((it) => {
          const isMine = it.created_by_admin_id === myAdminId;
          const isPending = it.status === 'pending';
          const canActOnIt = isPending && !isMine;
          return (
            <div key={it.action_id} className="bg-[#161b22] border border-[#30363d] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      {ACTION_TYPE_LABEL[it.action_type] || it.action_type}
                    </span>
                    <Badge className={`text-xs border-0 bg-[#0d1117] ${ACTION_STATUS_COLOR[it.status] || 'text-white'}`}>
                      {ACTION_STATUS_LABEL[it.status] || it.status}
                    </Badge>
                    {isMine && isPending && (
                      <Badge variant="secondary" className="bg-[#1a2332] text-[#8b949e] border-0 text-xs">
                        ваш запрос — ждёт второго админа
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-[#8b949e] mt-1">
                    Создал: {adminLabel(it.created_by_admin_id)}
                    {' · '}
                    <Clock className="w-3 h-3 inline -mt-0.5" />{' '}
                    {fmtDate(it.created_at)}
                    {isPending && (
                      <>
                        {' · истекает '}
                        {fmtDate(it.expires_at)}
                      </>
                    )}
                    {it.approved_by_admin_id && (
                      <>
                        {' · '}{it.status === 'rejected' ? 'отклонил' : 'одобрил'}:{' '}
                        {adminLabel(it.approved_by_admin_id)}
                      </>
                    )}
                  </div>
                  <div className="mt-2 bg-[#0d1117] border border-[#30363d] rounded p-2 text-xs font-mono text-[#c9d1d9] overflow-x-auto">
                    {JSON.stringify(it.payload, null, 0)}
                  </div>
                </div>
                {canActOnIt && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={() => setAction({ kind: 'approve', item: it })}
                      className="bg-[#ff6b00] hover:bg-[#e55a00] text-white text-xs h-8"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Одобрить
                    </Button>
                    <Button
                      onClick={() => setAction({ kind: 'reject', item: it })}
                      variant="outline"
                      className="border-red-900 text-red-400 hover:bg-red-950 text-xs h-8"
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Отклонить
                    </Button>
                  </div>
                )}
                {isPending && isMine && (
                  <div className="text-xs text-[#6e7681] shrink-0">
                    нельзя апрувить свой запрос
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TotpPromptDialog
        open={!!action}
        title={action?.kind === 'approve' ? 'Одобрить действие' : 'Отклонить действие'}
        description={
          action
            ? `${ACTION_TYPE_LABEL[action.item.action_type] || action.item.action_type} · введите ваш 2FA-код для подтверждения`
            : ''
        }
        confirmLabel={action?.kind === 'approve' ? 'Одобрить' : 'Отклонить'}
        destructive={action?.kind === 'reject'}
        loading={busy}
        onClose={() => setAction(null)}
        onSubmit={submit}
      />
    </div>
  );
}


// ── Admins tab (super_admin only) ─────────────────────────────────────────────

function AdminsTab() {
  const { user } = useAuth();
  const myUserId = user?.id;
  const [items, setItems] = useState<PcAdminItem[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoteUserId, setPromoteUserId] = useState('');
  const [promoteSuper, setPromoteSuper] = useState(false);
  const [promoting, setPromoting] = useState(false);
  // TOTP setup state
  const [totpData, setTotpData] = useState<TotpEnrollData | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);
  // Из items находим себя — нужно для «настроить 2FA» состояния.
  const me = items.find((a) => a.external_id === `monolith:${myUserId}`);
  const my2fa = me?.totp_enrolled_at;

  const load = async () => {
    setLoading(true);
    try {
      const resp = (await adminApi.listAdmins(includeInactive)) as PcAdminListResponse;
      setItems(resp.items || []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Не удалось загрузить список: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const onPromote = async () => {
    const userId = parseInt(promoteUserId, 10);
    if (Number.isNaN(userId) || userId <= 0) {
      toast.error('Введите корректный user_id');
      return;
    }
    setPromoting(true);
    try {
      await adminApi.promoteAdmin(userId, promoteSuper ? ['super_admin'] : []);
      toast.success(`Юзер ${userId} назначен админом`);
      setPromoteUserId('');
      setPromoteSuper(false);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Промоут не удался: ${msg}`);
    } finally {
      setPromoting(false);
    }
  };

  const onToggleSuper = async (a: PcAdminItem) => {
    const isSuper = a.scopes.includes('super_admin');
    const newScopes = isSuper
      ? a.scopes.filter((s) => s !== 'super_admin')
      : [...a.scopes, 'super_admin'];
    try {
      await adminApi.patchAdmin(a.admin_id, { scopes: newScopes });
      toast.success(isSuper ? 'super_admin снят' : 'super_admin выдан');
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Изменение не удалось: ${msg}`);
    }
  };

  const onRevoke = async (a: PcAdminItem) => {
    if (!confirm(`Revoke ${a.external_id} (${a.email || '—'})?`)) return;
    try {
      await adminApi.revokeAdmin(a.admin_id);
      toast.success('Доступ отозван');
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Revoke не удался: ${msg}`);
    }
  };

  const onTotpEnroll = async () => {
    setTotpBusy(true);
    try {
      const data = (await adminApi.totpEnroll()) as TotpEnrollData;
      setTotpData(data);
      setTotpCode('');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Не удалось сгенерировать ключ: ${msg}`);
    } finally {
      setTotpBusy(false);
    }
  };

  const onTotpVerify = async () => {
    if (!/^\d{6,8}$/.test(totpCode)) {
      toast.error('Код должен быть 6-8 цифр');
      return;
    }
    setTotpBusy(true);
    try {
      await adminApi.totpVerify(totpCode);
      toast.success('2FA активировано');
      setTotpData(null);
      setTotpCode('');
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Не верный код: ${msg}`);
    } finally {
      setTotpBusy(false);
    }
  };

  const onTotpReset = async (a: PcAdminItem) => {
    const isSelf = a.external_id === `monolith:${myUserId}`;
    const who = isSelf ? 'свой' : `${a.external_id}`;
    if (!confirm(`Сбросить ${who} 2FA? Потом нужна повторная настройка.`)) return;
    try {
      await adminApi.totpReset(a.admin_id);
      toast.success('2FA сброшено');
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      toast.error(`Reset не удался: ${msg}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* My 2FA card */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-5">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          {my2fa ? (
            <ShieldCheck className="w-5 h-5 text-green-500" />
          ) : (
            <KeyRound className="w-5 h-5 text-[#ff6b00]" />
          )}
          Двухфакторная аутентификация (TOTP)
        </h3>
        {my2fa ? (
          <div className="flex items-center gap-3 text-sm text-[#8b949e]">
            <span>
              Активировано {new Date(my2fa).toLocaleDateString()}. Используется
              при approve money-операций (4-eyes flow).
            </span>
            {me && (
              <Button
                onClick={() => void onTotpReset(me)}
                variant="outline"
                className="text-xs h-8 border-red-900 text-red-400 hover:bg-red-950"
              >
                Сбросить
              </Button>
            )}
          </div>
        ) : !totpData ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-[#8b949e] flex-1 min-w-[200px]">
              Без 2FA money-операции (approve refunds, wallet adjust) недоступны.
              Понадобится приложение Google Authenticator / Authy / 1Password.
            </p>
            <Button
              onClick={onTotpEnroll}
              disabled={totpBusy}
              className="bg-[#ff6b00] hover:bg-[#e55a00] text-white"
            >
              {totpBusy ? 'Генерация…' : 'Сгенерировать ключ'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-[#8b949e]">
              1. Отсканируй QR в приложении (или вставь secret вручную).
            </p>
            <div className="flex flex-wrap gap-4 items-start">
              {totpData.qr_data_url ? (
                <img
                  src={totpData.qr_data_url}
                  alt="2FA QR"
                  className="w-40 h-40 bg-white p-2 rounded-lg shrink-0"
                />
              ) : (
                <div className="w-40 h-40 bg-[#0d1117] border border-[#30363d] rounded-lg flex items-center justify-center text-xs text-[#8b949e]">
                  QR недоступен
                </div>
              )}
              <div className="flex-1 min-w-[240px] space-y-2">
                <div>
                  <label className="block text-xs text-[#8b949e] mb-1">otpauth URL (secret)</label>
                  <input
                    readOnly
                    value={totpData.secret}
                    className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-xs font-mono text-[#c9d1d9] focus:outline-none"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <p className="text-sm text-[#8b949e]">2. Введи 6-значный код из приложения:</p>
                <div className="flex gap-2">
                  <input
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="123456"
                    inputMode="numeric"
                    pattern="\d*"
                    className="bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm w-32 font-mono focus:outline-none focus:border-[#ff6b00]"
                  />
                  <Button
                    onClick={onTotpVerify}
                    disabled={totpBusy || totpCode.length < 6}
                    className="bg-[#ff6b00] hover:bg-[#e55a00] text-white"
                  >
                    {totpBusy ? 'Проверка…' : 'Активировать'}
                  </Button>
                  <Button
                    onClick={() => { setTotpData(null); setTotpCode(''); }}
                    variant="outline"
                    className="border-[#30363d] text-[#8b949e] hover:bg-[#21262d]"
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Promote form */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-5">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#ff6b00]" />
          Назначить админа
        </h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-[#8b949e] mb-1">user_id (монолит)</label>
            <input
              type="number"
              value={promoteUserId}
              onChange={(e) => setPromoteUserId(e.target.value)}
              placeholder="82"
              className="bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:border-[#ff6b00]"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#8b949e] py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={promoteSuper}
              onChange={(e) => setPromoteSuper(e.target.checked)}
              className="accent-[#ff6b00]"
            />
            super_admin (может управлять админами)
          </label>
          <Button
            onClick={onPromote}
            disabled={promoting || !promoteUserId}
            className="bg-[#ff6b00] hover:bg-[#e55a00] text-white"
          >
            {promoting ? 'Назначаем…' : 'Назначить'}
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-[#30363d]">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#ff6b00]" />
            Все админы {loading && <RefreshCw className="w-4 h-4 animate-spin text-[#8b949e]" />}
          </h3>
          <label className="flex items-center gap-2 text-sm text-[#8b949e] cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="accent-[#ff6b00]"
            />
            показывать отозванных
          </label>
        </div>

        {items.length === 0 && !loading && (
          <div className="p-6 text-center text-[#8b949e]">Админов нет</div>
        )}

        <div className="divide-y divide-[#30363d]">
          {items.map((a) => {
            const isSuper = a.scopes.includes('super_admin');
            const hasTotp = !!a.totp_enrolled_at;
            const isSelf = a.external_id === `monolith:${myUserId}`;
            const u = a.user;
            const displayName = u?.steam_name || a.email || a.external_id;
            // external_id уже использован как имя, если нет ни Steam-ника, ни email.
            const showExternalId = displayName !== a.external_id;
            return (
              <div
                key={a.admin_id}
                className={`p-4 flex items-start gap-4 flex-wrap transition-colors hover:bg-[#1a2332]/40 ${a.is_active ? '' : 'opacity-60'}`}
              >
                {/* Аватар + корона super_admin */}
                <div className="relative shrink-0">
                  {u?.steam_avatar ? (
                    <img
                      src={u.steam_avatar}
                      alt={displayName}
                      className="w-10 h-10 rounded-full border border-[#30363d] object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a2332] to-[#21262d] border border-[#30363d] flex items-center justify-center text-sm font-semibold text-[#8b949e]">
                      {u || a.email ? initialsFrom(displayName) : <UserIcon className="w-5 h-5" />}
                    </div>
                  )}
                  {isSuper && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#ff6b00] border-2 border-[#161b22] flex items-center justify-center"
                      title="super_admin"
                    >
                      <Crown className="w-2.5 h-2.5 text-white" />
                    </span>
                  )}
                </div>

                {/* Идентификация */}
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium truncate max-w-full">{displayName}</span>
                    {isSelf && (
                      <Badge variant="secondary" className="bg-[#1a2332] text-[#8b949e] border-0 text-xs">
                        вы
                      </Badge>
                    )}
                    {isSuper && (
                      <Badge className="bg-[#ff6b00] text-white border-0 text-xs">super_admin</Badge>
                    )}
                    {hasTotp ? (
                      <Badge className="bg-green-900/50 text-green-400 border border-green-900 text-xs">
                        <ShieldCheck /> 2FA
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-[#30363d] text-[#8b949e] border-0 text-xs">
                        без 2FA
                      </Badge>
                    )}
                    {!a.is_active && (
                      <Badge variant="secondary" className="bg-red-950/60 text-red-400 border border-red-900/50 text-xs">
                        отозван
                      </Badge>
                    )}
                  </div>

                  {/* Steam ID / external_id / ссылка на профиль */}
                  <div className="flex items-center gap-x-3 gap-y-1 mt-1 text-xs flex-wrap">
                    {u?.steam_id && (
                      <span className="font-mono text-[#8b949e]" title="Steam ID">
                        {u.steam_id}
                      </span>
                    )}
                    {showExternalId && (
                      <span className="font-mono text-[#6e7681]" title="payments-core external_id">
                        {a.external_id}
                      </span>
                    )}
                    {u?.steam_profile_url && (
                      <a
                        href={u.steam_profile_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#58a6ff] hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" /> Steam-профиль
                      </a>
                    )}
                  </div>

                  {/* Email + мета */}
                  <div className="flex items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-[#6e7681] flex-wrap">
                    {a.email && (
                      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
                        <Mail className="w-3 h-3 shrink-0" />
                        <span className="truncate">{a.email}</span>
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> Назначен {formatDate(a.created_at)}
                    </span>
                    {u?.last_activity && (
                      <span title={`Последняя активность: ${new Date(u.last_activity).toLocaleString('ru-RU')}`}>
                        был онлайн {relativeTime(u.last_activity)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Действия */}
                {a.is_active && (
                  <div className="flex gap-2 flex-wrap justify-end shrink-0">
                    {hasTotp && (
                      <Button
                        onClick={() => void onTotpReset(a)}
                        variant="outline"
                        className="text-xs h-8 border-[#30363d] hover:bg-[#21262d]"
                        title="Сбросить 2FA — потом нужна повторная настройка"
                      >
                        <KeyRound className="w-3 h-3 mr-1" />
                        Сбросить 2FA
                      </Button>
                    )}
                    <Button
                      onClick={() => onToggleSuper(a)}
                      variant="outline"
                      className="text-xs h-8 border-[#30363d] hover:bg-[#21262d]"
                    >
                      {isSuper ? 'Снять super' : 'Выдать super'}
                    </Button>
                    {!isSelf && (
                      <Button
                        onClick={() => onRevoke(a)}
                        variant="outline"
                        className="text-xs h-8 border-red-900 text-red-400 hover:bg-red-950"
                      >
                        <ShieldOff className="w-3 h-3 mr-1" />
                        Revoke
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'refunds' | 'orders' | 'users' | 'approvals' | 'audit' | 'maintenance' | 'admins';

export function AdminPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('refunds');

  useEffect(() => {
    if (!isAuthenticated || !user?.is_admin) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  if (!user?.is_admin) return null;

  const isSuper = !!user?.is_super_admin;
  const tabs: { key: Tab; label: string }[] = [
    { key: 'refunds', label: 'Возвраты' },
    { key: 'orders', label: '📦 Заказы' },
    { key: 'users', label: 'Пользователи' },
    { key: 'approvals', label: '🕒 Approvals' },
    { key: 'audit', label: 'Audit-лог' },
    { key: 'maintenance', label: '⚙️ Maintenance' },
    ...(isSuper ? [{ key: 'admins' as Tab, label: '🛡 Админы' }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0d1117] text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Панель администратора</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#161b22] p-1 rounded-xl w-fit border border-[#30363d] flex-wrap">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === key
                  ? 'bg-[#ff6b00] text-white'
                  : 'text-[#8b949e] hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'refunds' && <RefundsTab />}
        {tab === 'orders'  && <OrdersTab />}
        {tab === 'users'   && <UsersTab />}
        {tab === 'approvals' && <ApprovalsTab />}
        {tab === 'audit'   && <AuditTab />}
        {tab === 'maintenance' && <MaintenanceTab />}
        {tab === 'admins' && isSuper && <AdminsTab />}
      </div>
    </div>
  );
}
